<template>
  <div :class="classObj" class="app-wrapper">
    <div v-if="adminEnterBanner" class="admin-enter-banner">
      <span class="admin-enter-banner__text">管理端代登：{{ adminEnterSummary }}</span>
      <i class="el-icon-close admin-enter-banner__close" title="关闭" @click="dismissAdminEnterBanner"></i>
    </div>
    <div
      v-if="device === 'mobile' && sidebar.opened"
      class="drawer-bg"
      @click="handleClickOutside"
    />
    <sidebar class="sidebar-container"/>
    <div :class="{ hasTagsView: needTagsView }" class="main-container">
      <div :class="{ 'fixed-header': fixedHeader }">
        <navbar
          ref="navbar"
          :notifyUnread="notifyUnreads.length > 1"
          @clickNotifyItem="showNotifyDetail"
        />
        <tags-view v-if="needTagsView"/>
      </div>
      <app-main/>
    </div>
    <ai-assistant/>
    <el-dialog
      :visible.sync="visible.notify"
      width="720px"
      @close="handleNotifyClose"
      class="notify-dialog"
    >
      <div slot="title" class="notify-title">
        <svg-icon class-name="card-panel-icon" icon-class="icon-note"/>
        <span class="type-badge">{{ notifyTypes[notify.notifyType] }}</span>
      </div>
      <div class="notify-content" v-loading="loading.notify">
        <div v-if="!loading.notify">
          <h2 class="notify-heading">{{ notify.title }}</h2>
          <div class="notify-meta">
            <span class="publisher">{{ notify.publisher }}</span>
            <span class="time">{{ notify.createTime | parseTime("{y}-{m}-{d} {h}:{i}:{s}", "None") }}</span>
          </div>
          <div v-html="notify.content" class="ql-editor ql-snow notify-body"></div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          @click="showNextNotify"
          v-if="notifyUnreads.length > 1"
        >Next
        </el-button
        >
        <el-button @click="visible.notify = false">Close</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {AppMain, Navbar, Sidebar, TagsView} from "./components";
import ResizeMixin from "./mixin/ResizeHandler";
import Loading from "@//components/Loading";
import AiAssistant from "@/components/AiAssistant";
import {mapState} from "vuex";
import {notifyDetail, notifyUnreadListForPopup} from "@//api/dashboard";
import {
  isAdminEnter,
  isAdminEnterBannerVisible,
  dismissAdminEnterBanner as persistDismissAdminEnterBanner,
  shouldPromptAdminEnter,
  markAdminEnterPrompted,
  setAdminEnterEmail,
} from "@/utils/admin-enter";

export default {
  name: "Layout",
  components: {
    AppMain,
    Navbar,
    Sidebar,
    TagsView,
    Loading,
    AiAssistant,
  },
  mixins: [ResizeMixin],
  computed: {
    ...mapState({
      sidebar: (state) => state.app.sidebar,
      device: (state) => state.app.device,
      showSettings: (state) => state.settings.showSettings,
      needTagsView: (state) => state.settings.tagsView,
      fixedHeader: (state) => state.settings.fixedHeader,
      userInfo: (state) => state.user.info,
    }),
    classObj() {
      return {
        hideSidebar: !this.sidebar.opened,
        openSidebar: this.sidebar.opened,
        withoutAnimation: this.sidebar.withoutAnimation,
        mobile: this.device === "mobile",
      };
    },
    adminEnterBanner() {
      return this.adminEnterBannerVisible && this.userInfo && this.userInfo.type === 'USER'
    },
    adminEnterSummary() {
      const u = this.userInfo || {}
      const parts = []
      if (u.email) parts.push(u.email)
      if (u.username) parts.push(u.username)
      if (u.balance != null) parts.push('余额 $' + u.balance)
      return parts.length ? parts.join(' ｜ ') : '未知用户'
    },
  },
  data() {
    return {
      adminEnterFlag: isAdminEnter(),
      adminEnterBannerVisible: isAdminEnterBannerVisible(),
      notifyTypes: {
        SYS: "System notification",
        EMERGENCY: "Emegency notification",
        IMPORTANT: "Important reminder",
        HOLIDAY: "Holidays notification",
        NEW_FEATURE: "New feature",
        FEATURE_UPDATE: "Feature update",
        STOCK_ALERT: "Stock alert",
        INVENTORY_CHECK_ALERT: "Inventory check alert",
        REFUND: "Refund",
        SOURCE: "Sourcing request",
        BALANCE_ALERT: "Low balance alert",
        RATE_SYNC: "Shipping rate alert",
      },
      loading: {
        notify: false,
      },
      visible: {
        notify: false,
      },
      notifyUnreads: [],
      notify: {},
    };
  },
  created() {
    this.rememberAdminEnterEmail()
    // 管理端代登时先弹用户确认，确认后再拉系统通知，避免叠弹窗
    if (this.userInfo && this.userInfo.type == 'USER' && !isAdminEnter()) {
      this.loadNofiyUnread();
    }
  },
  mounted() {
    this.promptAdminEnter()
  },
  watch: {
    userInfo() {
      this.rememberAdminEnterEmail()
    },
  },
  methods: {
    dismissAdminEnterBanner() {
      persistDismissAdminEnterBanner()
      this.adminEnterBannerVisible = false
    },
    rememberAdminEnterEmail() {
      if (!isAdminEnter()) {
        return
      }
      const email = this.userInfo && this.userInfo.email
      if (email) {
        setAdminEnterEmail(email)
      }
    },
    promptAdminEnter() {
      if (!shouldPromptAdminEnter()) {
        if (this.adminEnterFlag && this.userInfo && this.userInfo.type === 'USER') {
          this.loadNofiyUnread()
        }
        return
      }
      const u = this.userInfo || {}
      if (!u || u.type !== 'USER') {
        return
      }
      markAdminEnterPrompted()
      const lines = [
        `<p style="margin:0 0 8px;"><b>邮箱：</b>${u.email || '-'}</p>`,
        `<p style="margin:0 0 8px;"><b>用户名：</b>${u.username || '-'}</p>`,
        `<p style="margin:0 0 8px;"><b>余额：</b>$${u.balance != null ? u.balance : '-'}</p>`,
        u.phone ? `<p style="margin:0;"><b>电话：</b>${u.phone}</p>` : '',
      ].filter(Boolean).join('')
      this.$alert(
        `<div style="line-height:1.6;">${lines}</div>`,
        '已从管理端进入该用户账号',
        {
          confirmButtonText: '确认',
          type: 'warning',
          dangerouslyUseHTMLString: true,
          distinguishCancelAndClose: false,
        }
      ).finally(() => {
        this.loadNofiyUnread()
      })
    },
    loadNofiyUnread() {
      notifyUnreadListForPopup().then(({data}) => {
        this.notifyUnreads = data;
        this.showUnreadNotify();
      });
    },
    showUnreadNotify() {
      if (this.notifyUnreads && this.notifyUnreads.length > 0) {
        this.notify = this.notifyUnreads[0];
        this.visible.notify = true;
      }
    },
    showNotifyDetail(id) {
      this.visible.notify = true;
      this.loading.notify = true;
      notifyDetail({id}).then(({data}) => {
        this.notify = data;
        this.loading.notify = false;
      });
    },
    showNextNotify() {
      const {id} = this.notify;
      this.$refs.navbar.notifyRead(id);
      this.notifyUnreads.splice(0, 1);
      this.notify = this.notifyUnreads[0];
    },
    handleClickOutside() {
      this.$store.dispatch("app/closeSideBar", {withoutAnimation: false});
    },
    handleNotifyClose() {
      if (!this.notify) {
        return;
      }
      const {id} = this.notify;
      this.$refs.navbar.notifyRead(id);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@//styles/mixin.scss";
@import "~@//styles/variables.scss";

.app-wrapper {
  @include clearfix;
  position: relative;
  height: 100%;
  width: 100%;

  &.mobile.openSidebar {
    position: fixed;
    top: 0;
  }
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

// 纯浮动覆盖，不改动页面高度，避免出现多余滚动条
.admin-enter-banner {
  position: fixed;
  top: 0;
  left: $sideBarWidth;
  right: 0;
  z-index: 2000;
  min-height: 48px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fdf6ec;
  color: #b88230;
  border-bottom: 1px solid #f5dab1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 10px 12px 10px 16px;
  transition: left var(--sidebar-motion-ms, 200ms) var(--sidebar-motion-ease, cubic-bezier(0.4, 0, 0.2, 1));
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;

  &__text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__close {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 700;
    color: #fff;
    background: #e6a23c;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #cf9236;
      color: #fff;
    }
  }
}

.hideSidebar .admin-enter-banner {
  left: $sideBarCollapsedWidth;
}

.mobile .admin-enter-banner {
  left: 0;
}

.fixed-header {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 9;
  width: calc(100% - #{$sideBarWidth});
  transition: width var(--sidebar-motion-ms, 200ms) var(--sidebar-motion-ease, cubic-bezier(0.4, 0, 0.2, 1));
}

.hideSidebar .fixed-header {
  width: calc(100% - #{$sideBarCollapsedWidth});
}

.mobile .fixed-header {
  width: 100%;
}

.notify-dialog {
  .notify-title {
    display: flex;
    align-items: center;
    gap: 8px;

    .type-badge {
      background: #fde8e9;
      color: #ed1c24;
      padding: 2px 10px;
      border-radius: 4px;
      font-size: 13px;
      font-weight: 500;
    }
  }
}

.notify-content {
  min-height: 300px;
  max-height: 480px;
  padding-right: 6px;
  color: #606266;
  font-size: 14px;
  word-break: break-word;
  overflow-y: auto;

  .notify-heading {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 12px;
    word-break: break-word;
  }

  .notify-meta {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #ebeef5;

    .publisher {
      font-weight: 500;
      color: #ed1c24;
    }

    .time {
      color: #909399;
      font-size: 12px;
    }
  }

  ::v-deep .notify-body {
    padding: 0;
    min-height: 200px;

    img {
      max-width: 100%;
    }
  }
}
</style>
